import type { CellViewWire } from '@badger-sentinel/protocol';
import { useMemo } from 'react';

import { ModifierLadder, BlockBar } from './ModifierLadder.js';
import { ConfirmInline } from '../chrome/ConfirmInline.js';
import { targetWords } from '../model/builder.js';
import { projectEscalation } from '../model/escalation.js';
import { ladderForOrder } from '../model/ladder.js';
import { missionLabel } from '../model/missions.js';
import { queuedMissionsByAsset, type OrderFeedback, type QueuedOrder } from '../model/queue.js';
import { useConsoleStore } from '../state/store.js';

/**
 * The orders-queue dock (design 424–467).
 *
 * Cards across the foot of the centre pane: drag to reorder, right-click for
 * actions, per-card status, and one success breakdown at a time.
 *
 * ## A rejection and a warning are two different things
 *
 * `zOrderRejection` says the server **did not store** the order. `zOrderWarning`
 * rides on one it *did* — RB §7.3 prices an ROE violation at 3 VP and doubles
 * its escalation rather than refusing it (M3-D13). So they get two treatments
 * that cannot be mistaken for one another: a rejection replaces the card's
 * status strip with the red block bar and the card reads as not in the turn; a
 * warning keeps the strip and adds an amber price line, and the card reads as in
 * the turn and expensive.
 *
 * ## Both are placed by key, never by position
 *
 * `OrderRejection.index` is an offset into the array the client sent, and the
 * queue may have been reordered since. `model/queue.ts` matches the response to
 * the send that produced it and hands this component a map keyed on the order's
 * stable key. A rejection that could not be matched is rendered in the header
 * rather than pinned to whichever card happens to be in that position.
 */

export interface QueueDockProps {
  readonly view: CellViewWire | null;
  readonly feedback: OrderFeedback;
  readonly onContextMenu: (key: string, x: number, y: number) => void;
}

export function QueueDock(props: QueueDockProps): JSX.Element | null {
  const queue = useConsoleStore((state) => state.queue);
  const open = useConsoleStore((state) => state.dockOpen);
  const setOpen = useConsoleStore((state) => state.setDockOpen);
  const setWhyKey = useConsoleStore((state) => state.setWhyKey);
  const whyKey = useConsoleStore((state) => state.whyKey);
  const confirm = useConsoleStore((state) => state.confirm);
  const requestConfirm = useConsoleStore((state) => state.requestConfirm);
  const clearConfirm = useConsoleStore((state) => state.clearConfirm);
  const cancelOrder = useConsoleStore((state) => state.cancelOrder);
  const editOrder = useConsoleStore((state) => state.editOrder);
  const dropQueued = useConsoleStore((state) => state.dropQueued);
  const showToast = useConsoleStore((state) => state.showToast);

  const view = props.view;

  const projection = useMemo(
    () =>
      view === null
        ? null
        : projectEscalation(
            view.theatre.escalationCounter,
            queue.map((entry) => entry.order.mission),
          ),
    [view, queue],
  );

  if (view === null) return null;

  const whyEntry = queue.find((entry) => entry.key === whyKey) ?? null;
  const whyLadder =
    whyEntry === null
      ? null
      : ladderForOrder(view, whyEntry.order, queuedMissionsByAsset(queue, whyEntry.key));

  return (
    <div
      data-testid="queue-dock"
      style={{
        position: 'relative',
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        borderTop: '1px solid var(--border-strong)',
      }}
    >
      {whyEntry !== null && whyLadder !== null && (
        <div
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            bottom: '100%',
            zIndex: 'var(--z-dock)',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: 560,
            maxHeight: 330,
            marginBottom: 8,
          }}
        >
          <ModifierLadder
            testId="why-popover"
            title={
              whyEntry.order.asset +
              ' · ' +
              missionLabel(whyEntry.order.mission) +
              ' · ' +
              whyLadder.headline
            }
            rows={whyLadder.rows}
            footnote={whyLadder.footnote}
            block={whyLadder.block}
            onClose={() => setWhyKey(null)}
          />
        </div>
      )}

      <button
        type="button"
        data-testid="dock-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          height: 30,
          padding: '0 12px',
          background: 'var(--surface-2)',
          border: 0,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flex: 'none',
            fontFamily: 'var(--font-data)',
            fontSize: 12,
            color: 'var(--text-3)',
            transform: open ? 'rotate(90deg)' : 'none',
          }}
        >
          ›
        </span>
        <span
          style={{
            flex: 'none',
            fontFamily: 'var(--font-ui)',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text)',
          }}
        >
          ORDERS QUEUE
        </span>
        <span
          data-testid="queue-count"
          style={{
            flex: 'none',
            fontFamily: 'var(--font-data)',
            fontSize: 13,
            fontWeight: 600,
            color: queue.length === 0 ? 'var(--text-muted)' : 'var(--primary)',
          }}
        >
          {queue.length}
        </span>
        {projection !== null && (
          <>
            <span
              aria-hidden="true"
              style={{ flex: 'none', width: 1, height: 13, background: 'var(--rule)' }}
            />
            <span
              style={{
                flex: 'none',
                fontFamily: 'var(--font-ui)',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '.14em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
              }}
            >
              ESC COST
            </span>
            <span
              data-testid="queue-escalation"
              style={{
                flex: 'none',
                fontFamily: 'var(--font-data)',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--warn)',
              }}
            >
              {projection.label}
            </span>
          </>
        )}
        <span style={{ flex: 1, minWidth: 8 }} />
        {props.feedback.unplaced.length > 0 && (
          <span
            data-testid="queue-unplaced"
            style={{
              flex: 'none',
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              letterSpacing: '.1em',
              color: 'var(--red-text)',
            }}
          >
            {String(props.feedback.unplaced.length) + ' REJECTED'}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="queue-cards"
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'stretch',
            gap: 8,
            /*
              122px is what an order card needs. An empty dock needs one line.

              It held the card height either way, so at the 1280 floor a seat that
              had drafted nothing spent 153px of the centre pane — more than the
              band strip, which carries the enemy picture — on the words NO ORDERS
              DRAFTED. The pane's other two children are `flex: 1 1 auto` above a
              floor, so the 88px this gives back lands on the board rather than on
              the gap. The dock still holds a box; it holds the one it needs.
            */
            height: queue.length === 0 ? 34 : 122,
            padding: '9px 12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            borderTop: '1px solid var(--rule)',
          }}
        >
          {queue.length === 0 && (
            <span
              data-testid="queue-empty"
              style={{
                display: 'flex',
                alignItems: 'center',
                fontFamily: 'var(--font-data)',
                fontSize: 11,
                letterSpacing: '.14em',
                color: 'var(--text-muted)',
              }}
            >
              NO ORDERS DRAFTED
            </span>
          )}
          {queue.map((entry) => (
            <OrderCard
              key={entry.key}
              view={view}
              entry={entry}
              feedback={props.feedback}
              queue={queue}
              confirming={confirm?.kind === 'cancel-order' && confirm.key === entry.key}
              onWhy={() => setWhyKey(whyKey === entry.key ? null : entry.key)}
              onEdit={() => editOrder(entry.key)}
              onRequestCancel={() => requestConfirm({ kind: 'cancel-order', key: entry.key })}
              onCancel={() => {
                cancelOrder(entry.key);
                showToast('ORDER CANCELLED', 'warn');
              }}
              onKeep={clearConfirm}
              onDropKey={(fromKey) => dropQueued(fromKey, entry.key)}
              onContextMenu={props.onContextMenu}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OrderCard(props: {
  readonly view: CellViewWire;
  readonly entry: QueuedOrder;
  readonly feedback: OrderFeedback;
  readonly queue: readonly QueuedOrder[];
  readonly confirming: boolean;
  readonly onWhy: () => void;
  readonly onEdit: () => void;
  readonly onRequestCancel: () => void;
  readonly onCancel: () => void;
  readonly onKeep: () => void;
  readonly onDropKey: (fromKey: string) => void;
  readonly onContextMenu: (key: string, x: number, y: number) => void;
}): JSX.Element {
  const { entry, view } = props;
  const rejection = props.feedback.rejections.get(entry.key) ?? null;
  const warnings = props.feedback.warnings.get(entry.key) ?? [];
  const ladder = ladderForOrder(view, entry.order, queuedMissionsByAsset(props.queue, entry.key));
  const target = targetWords(view, entry.order.target);

  return (
    <div
      data-testid="order-card"
      data-order-key={entry.key}
      data-order-asset={entry.order.asset}
      draggable
      onDragStart={(event) => event.dataTransfer.setData('text/plain', entry.key)}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const from = event.dataTransfer.getData('text/plain');
        if (from !== '') props.onDropKey(from);
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        props.onContextMenu(entry.key, event.clientX, event.clientY);
      }}
      style={{
        flex: 'none',
        width: 250,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderLeft: '2px solid ' + (rejection === null ? 'var(--primary)' : 'var(--red)'),
      }}
    >
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch' }}>
        <button
          type="button"
          data-testid="order-card-body"
          onClick={props.onEdit}
          title="EDIT IN BUILDER"
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: '8px 0 8px 10px',
            overflow: 'hidden',
            background: 'transparent',
            border: 0,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          {/* Two lines, not one. The design's card is 250 px and its mock ids are
              `CSG-5`; ours are `BLUE-INTERCEPTOR-AURELIA` and `CTC-2E105054`, and
              on one line the *target alias* is what gets clipped — the identifier
              an operator says out loud, which Phase 2 already refused to truncate
              in the roster row. So the two ids own a line each and the **mission
              name** is the thing that elides, because it is the one word of the
              three that a longer card would only repeat. */}
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, width: '100%' }}>
            <span
              style={{
                flex: 'none',
                fontFamily: 'var(--font-data)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--blue)',
              }}
            >
              {entry.order.asset}
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: 'right',
                fontFamily: 'var(--font-ui)',
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--primary)',
              }}
            >
              {missionLabel(entry.order.mission)}
            </span>
          </span>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, width: '100%' }}>
            <span
              style={{
                flex: 'none',
                fontFamily: 'var(--font-data)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--red)',
              }}
            >
              {target.id}
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontFamily: 'var(--font-prose)',
                fontSize: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--text-muted)',
              }}
            >
              {target.sub}
            </span>
          </span>
        </button>
        {!props.confirming && (
          <button
            type="button"
            data-testid="order-cancel"
            aria-label={'CANCEL ORDER ' + entry.order.asset}
            onClick={props.onRequestCancel}
            style={{
              flex: 'none',
              width: 26,
              background: 'transparent',
              border: 0,
              borderLeft: '1px solid var(--rule-faint)',
              color: 'var(--text-3)',
              fontSize: 15,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        )}
      </div>

      {rejection === null ? (
        <div
          data-testid="order-status"
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            height: 26,
            padding: '0 10px',
            background: 'var(--surface)',
            borderTop: '1px solid var(--rule-faint)',
          }}
        >
          <span
            style={{
              flex: 'none',
              fontFamily: 'var(--font-ui)',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            SUCCESS
          </span>
          <span
            data-testid="order-success"
            style={{
              flex: 'none',
              fontFamily: 'var(--font-data)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text)',
            }}
          >
            {ladder?.headline ?? '?'}
          </span>
          <button
            type="button"
            data-testid="order-why"
            onClick={props.onWhy}
            style={{
              flex: 'none',
              height: 18,
              padding: '0 6px',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              color: 'var(--text-3)',
              fontFamily: 'var(--font-data)',
              fontSize: 9.5,
              letterSpacing: '.08em',
              cursor: 'pointer',
            }}
          >
            WHY ›
          </button>
          <span style={{ flex: 1 }} />
          {ladder?.escalation != null && (
            <>
              <span
                style={{
                  flex: 'none',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '.14em',
                  color: 'var(--text-muted)',
                }}
              >
                ESC
              </span>
              <span
                data-testid="order-escalation"
                style={{
                  flex: 'none',
                  fontFamily: 'var(--font-data)',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--warn)',
                }}
              >
                {ladder.escalation}
              </span>
            </>
          )}
        </div>
      ) : (
        <BlockBar block={{ reason: rejection.reason, detail: rejection.detail }} />
      )}

      {warnings.map((warning) => (
        <div
          key={warning.warning + String(warning.index)}
          data-testid="order-warning"
          data-warning={warning.warning}
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            minHeight: 24,
            padding: '2px 10px',
            background: 'var(--surface)',
            borderTop: '1px solid var(--warn)',
          }}
        >
          <span
            style={{
              flex: 'none',
              fontFamily: 'var(--font-ui)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.14em',
              color: 'var(--warn)',
            }}
          >
            {warning.warning.replace(/_/g, ' ')}
          </span>
          <span
            data-testid="order-warning-price"
            style={{
              flex: 'none',
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--warn)',
            }}
          >
            {ladder?.roe == null
              ? ''
              : '−' + String(ladder.roe.vp) + ' VP · ESC ×' + String(ladder.roe.escalationFactor)}
          </span>
        </div>
      ))}

      {props.confirming && (
        <ConfirmInline
          testId="order-cancel-confirm"
          prompt="Cancel this order?"
          confirmLabel="CANCEL"
          cancelLabel="KEEP"
          onConfirm={props.onCancel}
          onCancel={props.onKeep}
        />
      )}
    </div>
  );
}
